import * as React from "react";
import { formatText } from "bernie-l10n";
import { observer } from "mobx-react";

import { ToolbarType, UitkToolbar } from "uitk-react-toolbar";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkDialogContent, UitkFullscreenDialog } from "uitk-react-dialog";
import {
  UitkCard,
  UitkCardContent,
  UitkCardTextSegment,
  UitkCardSectionSeparatorType
} from "uitk-react-cards";

import { UserReview } from "typings/microserviceModels/destination-user-reviews-flex-module";
import { FlexClickTracker } from "src/components/utility/analytics/FlexClickTracker/FlexClickTracker";

interface DestinationUserReviewsDialogProps {
  toggleAllDestinationReviews: () => void;
  showMoreReviews: () => void;
  reviews: UserReview[];
  maxVisibleReview: number;
  reviewDialogTitle: string;
  nameForTracking: string;
}

export const DestinationUserReviewsDialog = observer((props: DestinationUserReviewsDialogProps) => {
  const dialogProps = {
    dialogShow: false,
    className: "FullscreenReviewsContainer"
  };

  const toolBarAttributes = {
    type: ToolbarType.CLOSE,
    action: props.toggleAllDestinationReviews,
    header: props.reviewDialogTitle,
    iconLabel: "close reviews",
    className: "elevation-1"
  };

  const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

  return (
    <UitkFullscreenDialog {...dialogProps}>
      <UitkToolbar {...toolBarAttributes} />
      <UitkDialogContent>
        <div className="CommentStyleReviews">
          {props.reviews
            .slice(0, props.maxVisibleReview + 1)
            .map((review: UserReview, index: number) => CommentStyleReview(review, index))}
        </div>
        {props.reviews.length > props.maxVisibleReview && (
          <div className="uitk-type-center">
            <ButtonTracked
              onClick={props.showMoreReviews}
              text={formatText("destinationReviews.showMore")}
              rfrr="Dialog.ShowMore"
              moduleName={props.nameForTracking}
              linkName="RFRR Action Event"
            />
          </div>
        )}
      </UitkDialogContent>
    </UitkFullscreenDialog>
  );
});

function CommentStyleReview(review: UserReview, index: number) {
  const { createTime, reviewerDisplayName, postStayReviewId, review: reviewText, rating } = review;

  return (
    <UitkCard
      className="all-b-margin-four"
      key={postStayReviewId}
      data-testid={`comment-review-${index}`}
    >
      <UitkCardContent>
        <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP}>
          <div className="CommentStyleReview all-y-margin-two all-y-padding-two all-l-margin-two all-l-padding-three">
            {rating && (
              <h3 className="uitk-type-heading-400">{`${rating}/5 - ${formatText(
                `destinationReviews.descriptor.${rating}`
              )}`}</h3>
            )}
            <p className="uitk-type-paragraph-200">{reviewText}</p>
            <CommentAuthor createTime={createTime} reviewer={reviewerDisplayName} />
          </div>
        </UitkCardTextSegment>
      </UitkCardContent>
    </UitkCard>
  );
}

function CommentAuthor({ createTime, reviewer }: { createTime: Date; reviewer?: string }) {
  return (
    <p className="uitk-type-200">
      <span className="uitk-type-bold">
        {reviewer || formatText("destinationReviews.verified.traveler")}
      </span>
      {`, ${formatTimeSincePosted(createTime)}`}
    </p>
  );
}

function formatTimeSincePosted(createTime: Date) {
  const today = new Date();
  const datePosted = new Date(createTime);
  const elapsedTimeInSeconds = (today.getTime() - datePosted.getTime()) / 1000;

  const hoursPassed = Math.floor(elapsedTimeInSeconds / 3600);
  const daysPassed = Math.floor(elapsedTimeInSeconds / 86400);
  const weeksPassed = Math.floor(elapsedTimeInSeconds / 604800);
  const monthsPassed = Math.floor(elapsedTimeInSeconds / 2592000);

  if (hoursPassed === 0) {
    return formatText("destinationReviews.lessThanOneHourAgo");
  }
  if (hoursPassed === 1) {
    return formatText("destinationReviews.oneHourAgo");
  }
  if (hoursPassed < 24) {
    return formatText("destinationReviews.hoursAgo", hoursPassed);
  }
  if (daysPassed === 1) {
    return formatText("destinationReviews.oneDayAgo");
  }
  if (daysPassed < 7) {
    return formatText("destinationReviews.daysAgo", daysPassed);
  }
  if (weeksPassed === 1) {
    return formatText("destinationReviews.oneWeekAgo");
  }
  if (monthsPassed < 1) {
    return formatText("destinationReviews.weeksAgo", weeksPassed);
  }
  if (monthsPassed === 1) {
    return formatText("destinationReviews.oneMonthAgo");
  }
  if (monthsPassed < 12) {
    return formatText("destinationReviews.monthsAgo", monthsPassed);
  }
  if (monthsPassed === 12) {
    return formatText("destinationReviews.oneYearAgo");
  }

  return formatText("destinationReviews.overOneYearAgo");
}
